import { Link, useParams, Navigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, CheckCircle2, ShieldCheck, Clock, ArrowLeft } from "lucide-react";
import { SECTOR_BY_SLUG, SECTORS } from "@/lib/sectors";
import { SECTOR } from "@/constants/testIds";
import FAQAccordion from "@/components/FAQAccordion";
import TrustBar from "@/components/TrustBar";
import Seo, { legalServiceSchema, breadcrumbSchema } from "@/components/Seo";

export default function SectorPage() {
    const { slug } = useParams();
    const sector = SECTOR_BY_SLUG[slug];
    if (!sector) return <Navigate to="/" replace />;
    const Icon = Icons[sector.icon] || Icons.Scale;

    const related = SECTORS.filter((s) => s.slug !== slug).slice(0, 3);

    const faq = [
        { q: `Am I eligible for a ${sector.name} claim?`, a: `If any of the points above apply to your situation, you almost certainly qualify for a free assessment. Our specialists will confirm in writing — there&apos;s zero cost and zero obligation to proceed.` },
        { q: "How long will it take?", a: "Most claims in this category resolve within 8 – 16 weeks. We&apos;ll give you a realistic timeline upfront." },
        { q: "What does it cost?", a: "Nothing upfront. We work on No Win, No Fee — our success fee is capped within FCA limits and only applies if we win." },
        { q: "What information do I need to start?", a: "Your name, contact details, postcode and a few quick facts about the situation. That&apos;s it — under 60 seconds." },
    ];

    return (
        <>
            <Seo
                title={`${sector.name} Claims`}
                description={sector.tagline}
                path={`/sectors/${sector.slug}`}
                jsonLd={[
                    legalServiceSchema(sector),
                    breadcrumbSchema([
                        { name: "Home", path: "/" },
                        { name: "Claim types", path: "/" },
                        { name: sector.name, path: `/sectors/${sector.slug}` },
                    ]),
                ]}
            />
            {/* Hero */}
            <section className="relative overflow-hidden bg-navy-900 text-white">
                <div className="absolute inset-0 bg-mesh-emerald" />
                <div className="absolute inset-0 grid-bg-dark opacity-25" />

                <div className="container-x relative pt-12 lg:pt-20 pb-20 lg:pb-24">
                    <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-emerald-300 hover:text-emerald-200 mb-8">
                        <ArrowLeft className="w-4 h-4" /> All claim types
                    </Link>
                    <div className="grid lg:grid-cols-12 gap-12 items-center">
                        <div className="lg:col-span-7">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-xl bg-emerald-500 flex items-center justify-center">
                                    <Icon className="w-6 h-6 text-navy-900" />
                                </div>
                                <span className="text-eyebrow-light">{sector.short}</span>
                            </div>
                            <h1 data-testid={SECTOR.headerTitle} className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-display font-bold tracking-tight leading-[1.05]">
                                {sector.name} <span className="text-emerald-400">Claims.</span>
                            </h1>
                            <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-2xl">
                                {sector.tagline}
                            </p>
                            <div className="mt-8 flex flex-wrap gap-3">
                                <Link
                                    to={`/claim?sector=${slug}`}
                                    data-testid={SECTOR.detailCta}
                                    className="btn-primary text-base"
                                >
                                    Start your free check <ArrowRight className="w-4 h-4" />
                                </Link>
                                <Link to="/how-it-works" className="btn-ghost-dark text-base">How it works</Link>
                            </div>

                            <div className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm text-slate-300">
                                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> No upfront fees</div>
                                <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-emerald-400" /> 60-second check</div>
                                <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-400" /> FCA aware</div>
                            </div>
                        </div>

                        <div className="lg:col-span-5">
                            <div className="relative">
                                <div className="absolute -inset-2 bg-gradient-to-tr from-emerald-500/30 to-blue-500/20 rounded-3xl blur-2xl" />
                                <div className="relative bg-white rounded-3xl p-8 text-navy-900">
                                    <div className="text-eyebrow">Average payout</div>
                                    <div className="mt-2 text-4xl sm:text-5xl font-display font-bold tracking-tight">
                                        {sector.averagePayout}
                                    </div>
                                    <p className="mt-3 text-sm text-slate-500">Range based on settled ClaimHub UK cases. Individual outcomes vary.</p>

                                    <div className="mt-6 pt-6 border-t border-slate-100">
                                        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700 mb-3">You may qualify if</div>
                                        <ul className="space-y-2.5">
                                            {sector.bullets.slice(0, 3).map((b) => (
                                                <li key={b} className="flex items-start gap-2.5 text-sm text-slate-700">
                                                    <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                                                    <span>{b}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    <Link to={`/claim?sector=${slug}`} className="mt-6 w-full btn-primary text-sm">
                                        Begin my check <ArrowRight className="w-4 h-4" />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <TrustBar />

            {/* What we cover */}
            <section className="section-pad bg-white">
                <div className="container-x">
                    <div className="grid lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-5">
                            <span className="text-eyebrow">What we cover</span>
                            <h2 className="mt-3 text-3xl sm:text-4xl font-display font-bold tracking-tight text-navy-900">
                                Everything you need to know about {sector.name.toLowerCase()} claims.
                            </h2>
                            <p className="mt-5 text-base text-slate-600 leading-relaxed">{sector.intro}</p>
                        </div>
                        <div className="lg:col-span-7">
                            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-7 lg:p-8">
                                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700 mb-4">You may qualify if any apply</div>
                                <ul className="space-y-4">
                                    {sector.bullets.map((b) => (
                                        <li key={b} className="flex items-start gap-3 text-base text-navy-900">
                                            <div className="w-6 h-6 rounded-full bg-emerald-600 flex items-center justify-center shrink-0 mt-0.5">
                                                <CheckCircle2 className="w-4 h-4 text-white" />
                                            </div>
                                            <span className="leading-relaxed">{b}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <FAQAccordion items={faq} />

            {/* Related sectors */}
            <section className="section-pad bg-white">
                <div className="container-x">
                    <div className="flex items-end justify-between mb-10">
                        <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight text-navy-900">Other claim types we handle</h2>
                        <Link to="/" className="text-sm font-semibold text-emerald-700 hover:text-emerald-800">View all →</Link>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {related.map((r) => {
                            const RIcon = Icons[r.icon] || Icons.Scale;
                            return (
                                <Link key={r.slug} to={`/sectors/${r.slug}`} className="card-soft card-soft-hover p-6">
                                    <div className="w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                                        <RIcon className="w-6 h-6 text-emerald-600" />
                                    </div>
                                    <h3 className="mt-5 text-lg font-display font-semibold text-navy-900">{r.name}</h3>
                                    <p className="mt-2 text-sm text-slate-500">{r.short}</p>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>
        </>
    );
}
